import axios from "axios";

// --- API base URL resolution ---
// In any browser, we unconditionally use the RELATIVE "/api" path. Emergent's
// ingress always routes /api/* on the frontend's domain to the backend, so
// same-origin is guaranteed in both preview and production. Using a relative
// path means every API call is same-origin, which is the ONLY way to keep
// auth cookies working reliably under Safari ITP, Brave Shields, and any
// browser set to block 3rd-party cookies. We only fall back to the absolute
// REACT_APP_BACKEND_URL when there is no window (e.g. Node tests).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export const API_BASE = (typeof window !== "undefined" && window.location)
  ? "/api"
  : (BACKEND_URL ? `${BACKEND_URL}/api` : "/api");

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// --- Session guard ---
// On any 401 from an authenticated endpoint:
//   1. Try a silent /auth/refresh (the refresh cookie may still be valid).
//   2. If refresh succeeds, retry the original request once.
//   3. If refresh fails, forget the local session and bounce to /login —
//      this prevents the "empty roster + no error" illusion users hit when
//      their access cookie silently expires.
let refreshInflight = null;
const AUTH_FREE_PATHS = [
  "/auth/login",
  "/auth/register",
  "/auth/refresh",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/coaches/public",
];

function isAuthFree(url) {
  if (!url) return false;
  return AUTH_FREE_PATHS.some((p) => url.includes(p));
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    if (
      status !== 401 ||
      original._retried ||
      original._skipAuthRedirect ||
      isAuthFree(original.url || "")
    ) {
      return Promise.reject(error);
    }
    original._retried = true;
    try {
      refreshInflight = refreshInflight || api.post("/auth/refresh").finally(() => {
        // allow a fresh attempt next time
        setTimeout(() => { refreshInflight = null; }, 0);
      });
      await refreshInflight;
      return api(original);
    } catch (_) {
      // Refresh failed → session is truly gone. Bounce to /login preserving where
      // they were so we can send them back after sign-in.
      if (typeof window !== "undefined") {
        const onLogin = window.location.pathname.startsWith("/login");
        const here = window.location.pathname + window.location.search;
        if (!onLogin) {
          const qs = here && here !== "/" ? `?next=${encodeURIComponent(here)}` : "";
          window.location.replace(`/login${qs}`);
        } else {
          // Already on /login and we STILL got a 401 on a protected call.
          // That usually means the browser is rejecting our auth cookie
          // (Safari ITP / Brave Shields blocking cross-site cookies). Surface
          // a clear signal so the user isn't stuck silently in a "login loop".
          window.dispatchEvent(new CustomEvent("pbcoach:session-blocked"));
        }
      }
      return Promise.reject(error);
    }
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Alias used by ported VideoKinetix code — keeps both call styles working
export function errMsg(err) {
  return formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Something went wrong";
}
